import React from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { BLOGS, getBlogBySlug } from "./blogsData";

const Paragraphs = ({ items }) =>
  items.map((text, i) => (
    <p key={i} className="text-gray-300 leading-relaxed mb-4">
      {text}
    </p>
  ));

export default function BlogPost() {
  const { slug } = useParams();
  const blog = getBlogBySlug(slug);

  if (!blog) {
    return (
      <section className="bg-gray-900 text-white min-h-[70vh] flex flex-col items-center justify-center px-6 text-center">
        <h1 className="text-3xl font-semibold mb-3">Article not found</h1>
        <p className="text-gray-400 mb-8">
          This article may have been moved or renamed.
        </p>
        <Link
          to="/blog"
          className="bg-brand text-black font-semibold px-8 py-3 rounded-xl hover:bg-black hover:text-brand transition-all"
        >
          Back to all blogs
        </Link>
      </section>
    );
  }

  const related = BLOGS.filter((b) => b.slug !== blog.slug);

  return (
    <article className="bg-gray-900 text-white">
      <div className="max-w-3xl mx-auto px-6 pt-12 pb-16">
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 text-brand text-sm mb-8 hover:underline"
        >
          <ArrowLeft className="w-4 h-4" />
          All blogs
        </Link>

        <p className="text-sm text-brand mb-3">{blog.date}</p>
        <h1 className="text-3xl md:text-4xl font-bold leading-tight mb-8">
          {blog.title}
        </h1>

        <img
          src={blog.image}
          alt={blog.imageAlt}
          className="w-full rounded-xl mb-10 object-cover max-h-[420px]"
        />

        <Paragraphs items={blog.intro} />

        {blog.sections.map((section) => (
          <section key={section.heading} className="mt-10">
            <h2 className="text-xl md:text-2xl font-semibold text-brand mb-4">
              {section.heading}
            </h2>

            <Paragraphs items={section.before} />

            {section.bullets.length > 0 && (
              <ul className="list-disc list-inside text-gray-300 space-y-2 mb-4 marker:text-brand">
                {section.bullets.map((b) => (
                  <li key={b}>{b}</li>
                ))}
              </ul>
            )}

            <Paragraphs items={section.after} />
          </section>
        ))}

        <p className="mt-12 border-l-4 border-brand pl-5 text-gray-200 italic leading-relaxed">
          {blog.closing}
        </p>

        {related.length > 0 && (
          <div className="mt-16 pt-10 border-t border-gray-700">
            <h2 className="text-lg font-semibold mb-6">Keep reading</h2>
            <div className="grid gap-6 sm:grid-cols-2">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/blog/${r.slug}`}
                  className="bg-gray-800 rounded-xl p-5 hover:shadow-lg hover:shadow-brand/20 transition-all"
                >
                  <p className="text-xs text-brand mb-2">{r.date}</p>
                  <p className="font-medium leading-snug">{r.title}</p>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </article>
  );
}
